/**
 * `@shn/phg-client-web/recovery/threshold-combine` — Shamir Secret Sharing
 * over GF(2^8) byte-wise combine primitive.
 *
 * Step 3b implementation cycle Step 1 per recovery_flow_phase_b_step_3b_design.md
 * §2.1 (cryptographic primitive choice) + §2.3 (client-side combine API) v0.3.
 *
 * Field: GF(2^8) with canonical AES Rijndael field polynomial 0x11b. Each
 * Shamir share carries 32 independent byte-level y-coordinates; combine
 * runs Lagrange interpolation at x=0 byte-by-byte across all shares to
 * reconstruct the 32-byte secret.
 *
 * References:
 * - SLIP-0039 (Shamir's Secret-Sharing for Mnemonic Codes; production-tested
 *   in Trezor)
 * - HashiCorp Vault Shamir reference implementation
 * - draft-mcgrew-tss (Threshold Secret Sharing IETF draft)
 *
 * Cohort cohesion: client-side only. Substrate never combines shares per
 * Tech Spec §6.2.3 NO SHT BACKDOOR + ADR-026 §3 patient-fiduciary
 * non-delegable authority. No substrate-side mirror needed; binding tests
 * assert byte-for-byte parity vs canonical reference vectors.
 */
import { EnvelopeCryptoError } from '@shn/envelope-crypto';
/** Length of secret + each share's y-coordinate (combined-unwrap-material). */
const SECRET_BYTES = 32;
// GF(2^8) Rijndael field exp + log tables (generator g = 0x03; canonical AES field
// polynomial 0x11b). Computed at module load; pure-function deterministic.
const GF_EXP = new Uint8Array(512);
const GF_LOG = new Uint8Array(256);
(() => {
    let x = 1;
    for (let i = 0; i < 255; i++) {
        GF_EXP[i] = x;
        GF_LOG[x] = i;
        // Multiply by g=3: x = x*2 ^ x; reduce mod 0x11b if overflow.
        let next = x << 1;
        if (next & 0x100)
            next ^= 0x11b;
        next ^= x;
        x = next & 0xff;
    }
    // Extend exp table to index 255..510 to avoid modular reduction on multiply.
    for (let i = 255; i < 512; i++)
        GF_EXP[i] = GF_EXP[i - 255] ?? 0;
    GF_LOG[0] = 0; // log(0) is undefined; sentinel only — callers must guard.
})();
function gfMul(a, b) {
    if (a === 0 || b === 0)
        return 0;
    // Indexed access on Uint8Array under noUncheckedIndexedAccess returns number | undefined.
    // Inputs guaranteed in 0..255 by callers; tables sized 256/512.
    const logA = GF_LOG[a] ?? 0;
    const logB = GF_LOG[b] ?? 0;
    return GF_EXP[logA + logB] ?? 0;
}
function gfInv(a) {
    // a^-1 = g^(255 - log_g(a)) over GF(2^8) (Fermat's little theorem with order 255).
    if (a === 0) {
        throw new EnvelopeCryptoError('threshold-combine: GF inverse of zero is undefined', 'invalid-key');
    }
    const logA = GF_LOG[a] ?? 0;
    return GF_EXP[255 - logA] ?? 0;
}
/**
 * Combine k-of-n Shamir shares (GF(2^8) byte-wise) to reconstruct the secret.
 *
 * Failure modes (all internal; per `feedback_phg_recovery_ux_honesty.md`
 * patient-facing surface stays generic per memo §2.4):
 * - Insufficient shares (< threshold) → `EnvelopeCryptoError 'invalid-length'`
 * - Inconsistent y-coordinate length → `EnvelopeCryptoError 'invalid-length'`
 * - Duplicate share indices → `EnvelopeCryptoError 'invalid-key'`
 * - Out-of-range share index (0 or > 255) → `EnvelopeCryptoError 'invalid-key'`
 * - Non-integer share index → `EnvelopeCryptoError 'invalid-key'`
 *
 * Empirical adaptation note: memo §2.3 specifies `'invalid-input'` for the
 * structural-validity cases, but `EnvelopeCryptoErrorCode` does not include
 * that code; using `'invalid-key'` as closest semantic match (structural
 * validity issue at input parameter).
 */
export function combineShares(opts) {
    const { shares, threshold } = opts;
    if (!Number.isInteger(threshold) || threshold < 1) {
        throw new EnvelopeCryptoError(`threshold-combine: threshold must be a positive integer (got ${threshold})`, 'invalid-key');
    }
    if (shares.length < threshold) {
        throw new EnvelopeCryptoError(`threshold-combine: need ${threshold} shares, got ${shares.length}`, 'invalid-length');
    }
    // Take the first `threshold` shares; validate each.
    const taken = shares.slice(0, threshold);
    const seenIndices = new Set();
    for (const share of taken) {
        if (!Number.isInteger(share.index) || share.index < 1 || share.index > 255) {
            throw new EnvelopeCryptoError(`threshold-combine: share index out of range 1..255 (got ${share.index})`, 'invalid-key');
        }
        if (seenIndices.has(share.index)) {
            throw new EnvelopeCryptoError(`threshold-combine: duplicate share index ${share.index}`, 'invalid-key');
        }
        seenIndices.add(share.index);
        if (share.y.length !== SECRET_BYTES) {
            throw new EnvelopeCryptoError(`threshold-combine: share y-coordinate must be ${SECRET_BYTES} bytes (got ${share.y.length})`, 'invalid-length');
        }
    }
    // Lagrange interpolation at x=0 byte-by-byte.
    // secret[byte] = sum over i of (y_i[byte] * product over j!=i of (-x_j / (x_i - x_j)))
    // In GF(2), -x = x and subtraction = XOR, so coefficient simplifies to
    //   coeff_i = product over j!=i of (x_j / (x_i ^ x_j))
    const secret = new Uint8Array(SECRET_BYTES);
    for (let i = 0; i < taken.length; i++) {
        const share = taken[i];
        let coefficient = 1;
        for (let j = 0; j < taken.length; j++) {
            if (i === j)
                continue;
            const other = taken[j];
            coefficient = gfMul(coefficient, gfMul(other.index, gfInv(share.index ^ other.index)));
        }
        for (let b = 0; b < SECRET_BYTES; b++) {
            const yByte = share.y[b] ?? 0;
            const cur = secret[b] ?? 0;
            secret[b] = (cur ^ gfMul(yByte, coefficient)) & 0xff;
        }
    }
    return secret;
}
/**
 * Canonical wire-format byte length for an encoded `ShamirShare`:
 * `1` byte for `index` + `SECRET_BYTES` (32) for `y` = 33 bytes total.
 *
 * Per `recovery_flow_phase_b_step_3b_design.md` §2.1 + §2.3 v0.3
 * byte-wise GF(2^8) Shamir SSS: index occupies the GF(2^8) nonzero
 * element space `1..255` (single byte); `y` is 32 independent byte-
 * level y-coordinates (one per secret byte).
 */
export const SHAMIR_SHARE_ENCODED_BYTES = 1 + SECRET_BYTES;
/**
 * Encode a `ShamirShare` to its canonical 33-byte wire form for the
 * Layer 1 per-share AEAD plaintext (memo §2.2 v0.2 amendment):
 * `recoveryShareCiphertext_i = AEAD-AES-256-GCM(ShamirShare_i,
 * layerEvidenceKey_i, shareAad_i)`.
 *
 * Wire format (canonical, byte-for-byte parity with substrate-side
 * encoder when DEV3 ships the substrate-side mirror per `feedback_
 * canonical_mirror_drift_detection.md` discipline):
 *
 *   `[byte 0]`    — share index in `1..255` (GF(2^8) nonzero)
 *   `[bytes 1-32]` — 32-byte y-coordinate
 *
 * Validation matches `combineShares` invariants (index integer in
 * `1..255`; y exactly `SECRET_BYTES`).
 *
 * @throws {EnvelopeCryptoError} `'invalid-key'` if `index` is not an
 *   integer in `1..255`.
 * @throws {EnvelopeCryptoError} `'invalid-length'` if `y.length !== SECRET_BYTES`.
 */
export function encodeShamirShare(share) {
    if (!Number.isInteger(share.index) || share.index < 1 || share.index > 255) {
        throw new EnvelopeCryptoError(`encodeShamirShare: index out of range 1..255 (got ${share.index})`, 'invalid-key');
    }
    if (share.y.length !== SECRET_BYTES) {
        throw new EnvelopeCryptoError(`encodeShamirShare: y must be ${SECRET_BYTES} bytes (got ${share.y.length})`, 'invalid-length');
    }
    const out = new Uint8Array(SHAMIR_SHARE_ENCODED_BYTES);
    out[0] = share.index & 0xff;
    out.set(share.y, 1);
    return out;
}
/**
 * Decode a canonical 33-byte wire-form into a `ShamirShare`. Inverse
 * of `encodeShamirShare`.
 *
 * Used by `RecoveryFlow.submitLayer` per memo §3.4 worked example
 * Step 2 after AEAD-decrypting `recoveryShareCiphertext` with the
 * re-derived `layerEvidenceKey`. Returned share is pushed into the
 * in-flight transcript via the existing `pushCollectedShare`
 * lifecycle (active-session-only crypto per `feedback_phg_client_
 * active_session_crypto.md`).
 *
 * Returns a fresh `Uint8Array` for `y` so callers can hold or
 * zeroize it independent of the input plaintext buffer.
 *
 * @throws {EnvelopeCryptoError} `'invalid-length'` if `plaintext.length`
 *   is not exactly `SHAMIR_SHARE_ENCODED_BYTES` (33).
 * @throws {EnvelopeCryptoError} `'invalid-key'` if the decoded index
 *   is `0` (reserved for the secret per byte-wise Shamir SSS).
 */
export function decodeShamirShare(plaintext) {
    if (plaintext.length !== SHAMIR_SHARE_ENCODED_BYTES) {
        throw new EnvelopeCryptoError(`decodeShamirShare: plaintext must be ${SHAMIR_SHARE_ENCODED_BYTES} bytes (got ${plaintext.length})`, 'invalid-length');
    }
    const index = plaintext[0] ?? 0;
    if (index < 1 || index > 255) {
        throw new EnvelopeCryptoError(`decodeShamirShare: index out of range 1..255 (got ${index})`, 'invalid-key');
    }
    const y = new Uint8Array(SECRET_BYTES);
    y.set(plaintext.subarray(1, 1 + SECRET_BYTES));
    return { index, y };
}
//# sourceMappingURL=threshold-combine.js.map